
import { useState, useEffect } from "react"
import { useNavigate, Link, useLocation } from "react-router-dom"
import { Button } from "react-bootstrap"
import Select from "react-select"
import Loader from "../Common/Loader"
import { createContentAction, editContentAction } from "../../../store/actions/ContentBankAction"
import { handleError } from "../../../services/ErrorHandlingService"
import { useSelector, useDispatch } from "react-redux"
import { getDiseasesAction } from "../../../store/actions/DiseasesAction"

const AddContentBank = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const { item } = location.state || {}
  const diseases = useSelector((state) => state.diseases?.diseases || [])
  const isEditMode = !!item

  const [selectedDiseases, setSelectedDiseases] = useState([])
  const [errors, setErrors] = useState({});

  const [contentFile, setContentFile] = useState(null)
  const [existingFile, setExistingFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState(true);
  const [content, setContent] = useState({
    title: "",
    youtubeLink: "",
  })

  useEffect(() => {
    dispatch(getDiseasesAction(false)).then(() => {
      setFetching(false);
    });
  }, [dispatch])

  useEffect(() => {
    if (isEditMode && item) {
      console.log("Received item for edit mode:", item); // 🐞 Debug log

      setContent({
        title: item.content_title || "",
        youtubeLink: item.video_link || "",
      });

      if (Array.isArray(item.diseases)) {
        const mappedDiseases = item.diseases.map((d) => ({
          value: d.disease_id.toString(),
          label: d.disease_name,
        }));
        setSelectedDiseases(mappedDiseases);
      }

      if (item.content_file) {
        setExistingFile(item.content_file);
      }
    }
  }, [isEditMode, item]);



  const handleChange = (e) => {
    const { name, value } = e.target
    setContent({ ...content, [name]: value })
  }

  // CHANGE 2: Update handleFileChange to handle a single file
  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      const validTypes = ["image/png", "image/jpeg", "application/pdf"]
      if (!validTypes.includes(file.type)) {
        handleError("Please select a PNG, JPEG, or PDF file.")
        setContentFile(null) // Clear the file if invalid
        e.target.value = "" // Clear the input field
        return
      }
      setContentFile(file)
      setExistingFile(null)
    } else {
      setContentFile(null)
    }
  }
  useEffect(() => {
    return () => {
      if (contentFile) {
        URL.revokeObjectURL(contentFile);
      }
    };
  }, [contentFile]);


  const handleDiseaseChange = (selectedOptions) => {
    setSelectedDiseases(selectedOptions)
  }

  // CHANGE 3: Update determineContentType to use contentFile
  const determineContentType = (file, youtubeLink, existingFile) => {
    const validImageTypes = ["image/png", "image/jpeg"];
    const validPdfTypes = ["application/pdf"];

    if (file) {
      if (validImageTypes.includes(file.type)) return "Image";
      if (validPdfTypes.includes(file.type)) return "PDF"; // <-- ✅ not "Document"
    } else if (existingFile) {
      if (/\.(jpeg|jpg|png)$/i.test(existingFile)) return "Image";
      if (/\.pdf$/i.test(existingFile)) return "PDF"; // <-- ✅ not "Document"
    }
    return "";
  }

  const validateForm = () => {
    const newErrors = {};

    // if (!content.title.trim()) {
    //   newErrors.title = "Title is required";
    // }

    // if (!selectedDiseases.length) {
    //   newErrors.diseases = "Please select at least one disease";
    // }

    if (!contentFile && !existingFile) {
      newErrors.contentFile = "Please upload an image or PDF file.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return; // stop if validation fails
    }
    setLoading(true)

    // CHANGE 4: Check for contentFile instead of files.length
    if (!contentFile && !existingFile) {
      handleError("Please upload an image or PDF file.")
      setLoading(false)
      return
    }

    const validImageTypes = ["image/png", "image/jpeg"]
    const validPdfTypes = ["application/pdf"]

    // CHANGE 5: Validate the single contentFile
    if (contentFile && !(validImageTypes.includes(contentFile.type) || validPdfTypes.includes(contentFile.type))) {
      handleError("Please upload a valid image (PNG, JPG) or PDF file!")
      setLoading(false)
      return
    }

    const contentData = {
      content_id: isEditMode ? item.content_bank_id || item.content_id || item.id : undefined,
      content_title: content.title,
      video_link: content.youtubeLink,
      disease_ids: selectedDiseases.map((d) => d.value).join("|"),
      // CHANGE 6: Pass contentFile directly
      content_type: determineContentType(contentFile, content.youtubeLink, existingFile),
      content_file: contentFile, // Pass the single file here
    }
    console.log("TYPE CHECK:", {
      fileType: contentFile?.type,
      existingFile,
      determinedType: determineContentType(contentFile, content.youtubeLink, existingFile), // ✅ will show "PDF" now
    });


    console.log("Content bank ========>", contentData)

    try {
      const result = await dispatch(isEditMode ? editContentAction(contentData) : createContentAction(contentData))
      if (result?.success) {
        navigate("/content-bank")
      }
    } catch (err) {
      console.error(`${isEditMode ? "Update" : "Upload"} failed:`, err)
    } finally {
      setLoading(false)
    }
  }

  if (isEditMode && !item) {
    return (
      <div className="text-center my-5">
        <p>No content item selected for editing. Please go back to the content list.</p>
        <Link to="/content-list" className="btn btn-primary">
          Back to Content List
        </Link>
      </div>
    )
  }

  if (fetching) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
        }}
      >
        <Loader size="lg" />
      </div>
    )
  }

  return (
    <div className="row">
      <div className="col-xl-12">
        <div className="d-flex justify-content-end mb-3">
          <Link to="/content-bank" className="btn btn-link">
            <Button variant="primary">Back</Button>
          </Link>
        </div>
        <div className="card">
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-xl-6 mb-3">
                  <label htmlFor="title" className="form-label">
                    Title
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="title"
                    placeholder="Enter Title"
                    name="title"
                    value={content.title}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-xl-6 mb-3">
                  <label className="form-label">Disease</label>
                  <Select
                    isMulti
                    name="disease"
                    options={diseases.map((d) => ({
                      value: String(d.disease_id),
                      label: d.disease_name,
                    }))}
                    value={selectedDiseases}
                    onChange={handleDiseaseChange}
                    placeholder="Select Disease(s)"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: "55px",
                        height: "55px",
                      }),
                      multiValue: (provided) => ({
                        ...provided,
                        backgroundColor: "#e0e0e0",
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        padding: "4px",
                      }),
                      indicatorSeparator: (provided) => ({
                        ...provided,
                        display: "none",
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        lineHeight: "50px",
                      }),
                    }}
                  />
                </div>
              </div>
              <div className="row">
                <div className="row">
                  {/* <div className="col-xl-12 mb-3">
                    <label className="form-label">Upload Image / PDF</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/png, image/jpeg, application/pdf"
                      onChange={handleFileChange}
                    />

                   
                    {isEditMode && existingFile && !contentFile && (
                      <p className="mt-2 text-muted">
                        Current file:{" "}
                        <a href={existingFile} target="_blank" rel="noopener noreferrer">
                          {existingFile.split("/").pop()}
                        </a>{" "}
                        (Upload new file to replace)
                      </p>
                    )}

                 
                    {contentFile && (
                      <p className="mt-2 text-muted">New file selected: {contentFile.name}</p>
                    )}

                  
                    {(contentFile || existingFile) &&
                      (() => {
                        let previewURL = "";
                        let isImage = false;

                        if (contentFile) {
                          isImage = contentFile.type?.startsWith("image/");
                          previewURL = URL.createObjectURL(contentFile);
                        } else if (existingFile) {
                          isImage = /\.(jpeg|jpg|png)$/i.test(existingFile);
                          previewURL = existingFile;
                        }

                        return isImage ? (
                          <div className="mt-3 text-center">
                            <img
                              src={previewURL}
                              alt="Preview"
                              style={{
                                maxHeight: "200px",
                                borderRadius: "8px",
                                objectFit: "cover",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none"; // prevent broken image icon
                              }}
                            />
                          </div>
                        ) : (
                          existingFile && !contentFile && (
                            <div className="mt-3">
                              <p className="text-muted">Preview not available for non-image files.</p>
                            </div>
                          )
                        );
                      })()}

                  </div> */}
                  <div className="col-xl-12 mb-3">
                    <label className="form-label">
                      Upload Image / PDF {!isEditMode && <span className="text-danger">*</span>}
                    </label>

                    <div className="d-flex flex-wrap align-items-center gap-2">
                      {/* File name display + hidden input trigger */}
                      <div style={{ position: "relative", flex: 1 }}>
                        <input
                          type="text"
                          className="form-control"
                          value={
                            contentFile
                              ? contentFile.name
                              : existingFile
                                ? existingFile.split("/").pop()
                                : ""
                          }
                          readOnly
                          placeholder="Choose a file"
                          style={{
                            backgroundColor: "#f8f9fa",
                            cursor: "pointer",
                            paddingRight: "110px",
                          }}
                          onClick={() => document.getElementById("fileInput").click()}
                        />

                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          style={{
                            position: "absolute",
                            right: "5px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            fontSize: "12px",
                          }}
                          onClick={() => document.getElementById("fileInput").click()}
                        >
                          {contentFile || existingFile ? "Change File" : "Choose File"}
                        </button>

                        <input
                          id="fileInput"
                          type="file"
                          style={{ display: "none" }}
                          accept="image/png, image/jpeg, application/pdf"
                          onChange={handleFileChange}
                        />
                      </div>

                      {/* Preview Button */}
                      {(contentFile || existingFile) && (() => {
                        let previewURL = "";

                        if (contentFile) {
                          previewURL = URL.createObjectURL(contentFile);
                        } else if (existingFile) {
                          previewURL = existingFile;
                        }

                        return (
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            // style={{
                            //   fontSize: "12px",
                            //   whiteSpace: "nowrap",
                            //   marginTop: "1px",
                            //   marginLeft: "15px", 
                            // }}
                            onClick={() => window.open(previewURL, "_blank")}
                            title="Preview File"
                          >
                            Preview
                          </button>
                        );
                      })()}
                    </div>

                    {errors.contentFile && <div className="text-danger mt-1">{errors.contentFile}</div>}
                  </div>



                </div>

              </div>
              <div className="row">
                <div className="col-xl-12 mb-3">
                  <label htmlFor="youtubeLink" className="form-label">
                    YouTube Link (Optional)
                  </label>
                  <input
                    type="url"
                    className="form-control"
                    id="youtubeLink"
                    placeholder="Enter YouTube Link"
                    name="youtubeLink"
                    value={content.youtubeLink}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading || fetching}>
                {loading ? (
                  <>
                    <Loader size="sm" />
                    <span className="ms-2">{isEditMode ? "Updating..." : "Uploading..."}</span>
                  </>
                ) : (
                  <>{isEditMode ? "Update Content" : "Add Content"}</>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddContentBank;
